import { useQuery } from '@tanstack/react-query';

import { usePerpsWaitlistAccessSnapshot } from 'app/(main)/perps/usePerpsWaitlistAccess';
import { exchangeApi } from 'app/(trade)/queries/api';
import { useAuthStore } from 'lib/stores/authStore';

export interface MarginEnabledResponse {
    enabled: boolean;
}

type UseMarginEnabledOptions = {
    requireReleasedAccess?: boolean;
};

export const marginEnabledKeys = {
    all: ['marginEnabled'] as const,
    user: (userId: string) => [...marginEnabledKeys.all, userId] as const,
};

export const getMarginEnabled = async (signal?: AbortSignal): Promise<MarginEnabledResponse> => {
    const response = await exchangeApi.get<MarginEnabledResponse>('/trade-api/v2/margin/enabled', { signal });
    return response.data;
};

const PERPS_ACCESS_OFF_DATA: MarginEnabledResponse = { enabled: false };

export const useMarginEnabled = ({ requireReleasedAccess = true }: UseMarginEnabledOptions = {}) => {
    const userId = useAuthStore((s) => s.userId);
    const isMember = useAuthStore((s) => s.isLoggedInMember);
    const { hasReleasedAccess: perpsAccessEnabled, isLoading: perpsAccessLoading } = usePerpsWaitlistAccessSnapshot();
    const releasedAccessReady = requireReleasedAccess ? !perpsAccessLoading && perpsAccessEnabled : true;
    const queryEnabled = releasedAccessReady && !!userId && isMember;
    const query = useQuery({
        queryKey: marginEnabledKeys.user(userId!),
        queryFn: ({ signal }) => getMarginEnabled(signal),
        enabled: queryEnabled,
        staleTime: 60_000,
    });

    if (requireReleasedAccess && perpsAccessLoading) {
        return { ...query, data: undefined, isFetched: false, isLoading: true };
    }

    if (query.data?.enabled === true) {
        return query;
    }

    // Perps access off ⇒ margin cannot apply. Report fetched+{enabled:false} so
    // downstream `isLoaded` predicates (useAccountSummaryBalances, useHasFunds)
    // short-circuit via their existing `!marginEnabled || marginBalanceFetched`
    // branch instead of stalling on a query that will never run.
    if (requireReleasedAccess && !perpsAccessEnabled) {
        return { ...query, data: PERPS_ACCESS_OFF_DATA, isFetched: true };
    }
    return query;
};
